import { useEffect, useMemo, useState } from "react";
import { Badge, Group, Loader, SimpleGrid, Stack, Text } from "@mantine/core";
import { PageShell } from "@/components/PageShell";
import { Section } from "@/components/Section";
import { KpiCard } from "@/components/KpiCard";
import { LoadingState, ErrorState } from "@/components/LoadingState";
import { SortableTable, type Column } from "@/components/SortableTable";
import { DeploymentPicker } from "@/components/DeploymentPicker";
import { DeploymentProgressBar } from "@/components/DeploymentProgressBar";
import {
  useCvUpdateRequests,
  useCvUpdateResponses,
  useStoreInventory,
} from "@/lib/queries";
import {
  deploymentProgress,
  filterDeployments,
  fleetSids,
  formatElapsed,
  isDeploymentLive,
  NO_DEPLOYMENT_FILTERS,
  relativeTime,
  statusColor,
  statusLabel,
  type DeploymentFilters,
  type StoreProgress,
} from "@/lib/deployments";
import { fmt } from "@/lib/format";

/** A store with no response yet sorts last on status, not first. */
const STATUS_ORDER: Record<string, number> = {
  FAILED: 0,
  IN_PROGRESS: 1,
  SUCCEEDED: 2,
  SKIPPED: 3,
};

const COLUMNS: Column<StoreProgress>[] = [
  {
    key: "name",
    label: "Store",
    get: (row) => row.name ?? row.sid,
    render: (row) => (
      <Stack gap={0}>
        <Text size="sm">{row.name ?? "—"}</Text>
        <Text size="xs" c="dimmed">
          {row.sid}
        </Text>
      </Stack>
    ),
  },
  {
    key: "status",
    label: "Status",
    get: (row) => (row.status ? (STATUS_ORDER[row.status] ?? 4) : 5),
    render: (row) =>
      row.status ? (
        <Badge color={statusColor(row.status)} variant="light">
          {statusLabel(row.status)}
        </Badge>
      ) : (
        <Badge color="gray" variant="outline">
          No response
        </Badge>
      ),
  },
  {
    key: "startedAt",
    label: "Started",
    get: (row) => row.startedAt,
    render: (row) => fmt.dateTime(row.startedAt),
  },
  {
    key: "updatedAt",
    label: "Last event",
    get: (row) => row.updatedAt,
    render: (row) => relativeTime(row.updatedAt),
  },
  {
    key: "elapsedMs",
    label: "Took",
    numeric: true,
    get: (row) => row.elapsedMs,
    render: (row) => formatElapsed(row.elapsedMs),
  },
];

/**
 * Watch one CV deployment roll out across the fleet.
 *
 * Pick a deployment, then a bar that fills as base stations report in. The
 * selection is a `request_id`, which is both what Lebron joins responses on and
 * the only stable name a deployment has.
 */
export function DeploymentsPage() {
  const [filters, setFilters] = useState<DeploymentFilters>(
    NO_DEPLOYMENT_FILTERS,
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const requests = useCvUpdateRequests();
  const inventory = useStoreInventory();

  const all = useMemo(() => requests.data ?? [], [requests.data]);
  const matching = useMemo(
    () => filterDeployments(all, filters),
    [all, filters],
  );

  // Default to the newest deployment that matches, and re-default whenever the
  // current selection drops out of the filtered list — otherwise narrowing the
  // filters would leave the page showing a deployment the dropdown no longer
  // offers.
  useEffect(() => {
    if (selectedId && matching.some((r) => r.request_id === selectedId)) return;
    setSelectedId(matching[0]?.request_id ?? null);
  }, [matching, selectedId]);

  const selected = matching.find((r) => r.request_id === selectedId);

  const fleet = useMemo(() => fleetSids(inventory.data), [inventory.data]);

  // Responses are fetched before progress can say whether the deployment is
  // live, and the poll interval depends on that answer — so the hook is driven
  // by the *previous* render's verdict. One extra poll after a deploy finishes
  // is the cost, and that poll is what confirms it finished.
  const [live, setLive] = useState(true);
  const responses = useCvUpdateResponses(selectedId, live);

  const progress = useMemo(
    () => deploymentProgress(selected, responses.data ?? [], fleet),
    [selected, responses.data, fleet],
  );

  const stillLive = isDeploymentLive(selected, progress);
  useEffect(() => setLive(stillLive), [stillLive]);

  const subtitle = selected
    ? `${selected.blast ? "Fleet blast" : `Targeted at ${selected.store?.name ?? selected.sid}`} · started ${fmt.dateTime(selected.created_at)}`
    : "Pick a deployment to watch it roll out";

  const body = () => {
    if (requests.isLoading)
      return <LoadingState label="Loading deployments…" />;
    if (requests.error) return <ErrorState error={requests.error} />;

    return (
      <Stack gap="lg">
        <DeploymentPicker
          requests={all}
          matching={matching}
          filters={filters}
          onFiltersChange={setFilters}
          selected={selectedId}
          onSelect={setSelectedId}
        />

        {!selected ? (
          <Text c="dimmed">
            {all.length === 0
              ? "No CV deployments in the last 14 days."
              : "No deployment matches these filters."}
          </Text>
        ) : (
          <>
            <Section
              title={
                <Group gap="xs">
                  <span>Progress</span>
                  {live ? (
                    <Group gap={6}>
                      <Loader size="xs" />
                      <Text size="xs" c="dimmed">
                        polling every 10s
                      </Text>
                    </Group>
                  ) : (
                    <Badge color="gray" variant="light" size="sm">
                      {progress.isComplete ? "Complete" : "Idle"}
                    </Badge>
                  )}
                </Group>
              }
              subtitle={
                selected.blast
                  ? "Out of every active, tagged store — the same set the Ansible base-station inventory builds, which is what a blast addresses."
                  : "A targeted deploy addresses one store."
              }
            >
              {responses.error ? (
                <ErrorState error={responses.error} />
              ) : (
                <DeploymentProgressBar progress={progress} live={live} />
              )}
            </Section>

            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
              <KpiCard
                label="CV image"
                value={selected.cv_pipeline_image_tag}
                sub="cv-pipeline"
              />
              <KpiCard
                label="Triton"
                value={selected.triton_model_repo_version}
                sub="model repo"
              />
              <KpiCard
                label="Release"
                value={selected.ansible_ref ?? "—"}
                sub={selected.author ? `by ${selected.author}` : null}
              />
              <KpiCard
                label="Last event"
                value={relativeTime(progress.lastEventAt)}
                sub={`${progress.reported} store${progress.reported === 1 ? "" : "s"} reported`}
              />
            </SimpleGrid>

            <Section
              title="Stores"
              subtitle="One row per store, showing its latest event. Responses are append-only, so a store that has already started appears once, not twice."
            >
              {responses.isLoading && !responses.data ? (
                <LoadingState label="Loading responses…" />
              ) : (
                <SortableTable
                  columns={COLUMNS}
                  rows={progress.stores}
                  rowKey="sid"
                  initialSortKey="status"
                  initialSortDir={1}
                  pageSize={25}
                  emptyMessage="No store has reported for this deployment yet."
                />
              )}
            </Section>
          </>
        )}
      </Stack>
    );
  };

  return (
    <PageShell title="CV deployments" subtitle={subtitle}>
      {body()}
    </PageShell>
  );
}
